import { Box, useTheme } from "@mui/material";
import type { Palette } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import {
  levelOf,
  type GroupCategory,
  type RatingScale,
} from "@thalamos/haven-common";

import type { Swatch } from "../../theme/defaultTheme.js";

/** The colours a rating in a kind of domain is drawn in. */
export const swatchOf = ({
  colours,
  category,
  level,
}: {
  colours: Palette["rating"];
  category: GroupCategory;
  /** Where the answer stands among the ratings 0 to 3, as `levelOf` gives it. */
  level: number;
}): Swatch => {
  if (category === "strengths" && (level === 0 || level === 1)) {
    return colours.strengthToUse[level];
  }

  const { level3, level2 } = colours.category[category];

  if (level >= 3) {
    return level3;
  }

  return level === 2 ? level2 : colours.quiet;
};

const SIZES = {
  large: { width: 24, height: 24, fontSize: "0.8125rem" },
  small: { width: 20, height: 20, fontSize: "0.6875rem" },
  wide: { width: 36, height: 24, fontSize: "0.8125rem" },
} as const;

/** How an answer is written: yes or no on a yes/no scale, the number on a rating. */
export const useAnswerWords = () => {
  const { t } = useTranslation();

  return ({ scale, rating }: { scale: RatingScale; rating: number }): string =>
    match(scale)
      .with("rating", () => String(rating))
      .with("yes_no", () =>
        rating === 1 ? t("assessment.answer.yes") : t("assessment.answer.no"),
      )
      .exhaustive();
};

/**
 * A rating drawn as a small badge in its domain's colours, or quietly where
 * the domain's kind is not known or nothing was rated. On a yes/no scale it
 * says yes or no, coloured at the answer's `levelOf`.
 *
 * Hidden from a screen reader, so a caller must give the rating in words
 * beside it.
 */
export const RatingBadge = ({
  category,
  rating,
  scale,
  size = "large",
}: {
  category: GroupCategory | undefined;
  rating: number | undefined;
  scale: RatingScale;
  /** `wide` where the badge leads a rating's words on one line. */
  size?: "large" | "small" | "wide";
}) => {
  const wordOf = useAnswerWords();
  const colours = useTheme().palette.rating;
  const swatch =
    rating === undefined || category === undefined
      ? colours.quiet
      : swatchOf({ colours, category, level: levelOf({ scale, rating }) });
  const { width, height, fontSize } = SIZES[size];

  return (
    <Box
      aria-hidden
      sx={{
        minWidth: width,
        height,
        // A word wants room either side; a digit fills the square.
        px: match(scale)
          .with("yes_no", () => 0.5)
          .with("rating", () => 0)
          .exhaustive(),
        flexShrink: 0,
        borderRadius: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize,
        bgcolor: swatch.background,
        color: swatch.color,
      }}
    >
      {rating === undefined ? "–" : wordOf({ scale, rating })}
    </Box>
  );
};
